import { create } from "zustand";
import { api } from "@/lib/api";
import type { Agent, AgentTemplate } from "@/types/agent";

interface AgentState {
  agents: Agent[];
  templates: AgentTemplate[];
  isLoading: boolean;
  error: string | null;
}

interface AgentActions {
  loadAgents(): Promise<void>;
  loadTemplates(): Promise<void>;
  createAgent(config: Omit<Agent, "id" | "createdAt">): Promise<Agent | null>;
  updateAgent(id: string, config: Partial<Agent>): Promise<void>;
  deleteAgent(id: string): Promise<void>;
}

export const useAgentStore = create<AgentState & AgentActions>((set) => ({
  agents: [],
  templates: [],
  isLoading: false,
  error: null,

  async loadAgents() {
    set({ isLoading: true, error: null });
    try {
      const agents = await api.get<Agent[]>("/api/dashboard/agents");
      set({ agents, isLoading: false });
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : "Failed to load agents",
      });
    }
  },

  async loadTemplates() {
    try {
      const templates = await api.get<AgentTemplate[]>(
        "/api/dashboard/agents/templates",
      );
      set({ templates });
    } catch (err) {
      set({
        error:
          err instanceof Error ? err.message : "Failed to load templates",
      });
    }
  },

  async createAgent(config) {
    set({ isLoading: true, error: null });
    try {
      const agent = await api.post<Agent>("/api/dashboard/agents", config);
      set((state) => ({
        agents: [...state.agents, agent],
        isLoading: false,
      }));
      return agent;
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : "Failed to create agent",
      });
      return null;
    }
  },

  async updateAgent(id, config) {
    set({ isLoading: true, error: null });
    try {
      const updated = await api.patch<Agent>(
        `/api/dashboard/agents/${id}`,
        config,
      );
      set((state) => ({
        agents: state.agents.map((a) => (a.id === id ? updated : a)),
        isLoading: false,
      }));
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : "Failed to update agent",
      });
    }
  },

  async deleteAgent(id) {
    set({ isLoading: true, error: null });
    try {
      await api.delete(`/api/dashboard/agents/${id}`);
      set((state) => ({
        agents: state.agents.filter((a) => a.id !== id),
        isLoading: false,
      }));
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : "Failed to delete agent",
      });
    }
  },
}));
